/**
 * O aviso de "algo mudou", agora que o banco deixou de dá-lo.
 *
 * Até a Fase 12, a reatividade era de graça e vinha do lugar certo: o `useLiveQuery` do
 * Dexie observava as tabelas lidas lá no fundo, dentro do repositório, e toda tela se
 * atualizava sozinha — inclusive quando quem gravava era outra aba. O banco era o cache, e
 * ele era reativo (ADR-103).
 *
 * Sobre HTTP isso deixa de existir, e o plano já sabia: a estratégia decidida é **aviso +
 * rebusca da tela** (RN-152), e a parte do aviso — a que viria do servidor — continua em
 * aberto, porque as três formas de fazê-la foram recusadas por bons motivos (§Reatividade).
 *
 * O que este arquivo entrega é a **metade que não depende do servidor**: quem escreve
 * avisa, e quem lê rebusca. Cobre o caso comum, que é a própria pessoa mexendo no sistema
 * — a tela responde à ação dela como sempre respondeu.
 *
 * ## O que ele **não** cobre, e é preciso dizer em voz alta
 *
 * A gravação de **outra pessoa**. Duas pessoas na mesma obra deixam de ver uma a outra até
 * que alguém navegue ou aja. É a regressão que a escolha de 02/09/2026 assumiu, e ela some
 * no dia em que houver canal de aviso do servidor: este mesmo barramento passa a ter uma
 * segunda origem, e nenhuma tela precisa mudar.
 */

type Ouvinte = () => void;

const ouvintes = new Set<Ouvinte>();

/**
 * O canal entre abas.
 *
 * `BroadcastChannel` é browser-puro e não custa requisição nenhuma, então a parte da
 * reatividade que o Dexie dava **entre abas do mesmo navegador** volta inteira. É pouco
 * perto do que se perdeu, mas é o pedaço que dá para recuperar sem servidor — e é o mais
 * frequente: a mesma pessoa com o cronograma aberto numa aba e a conferência na outra.
 *
 * Ausente em ambiente de teste e em navegador antigo, e a falta dele não pode derrubar
 * nada: sem canal, o aviso continua valendo dentro da própria aba.
 */
const CANAL = 'obraplan:mudou';

const canal: BroadcastChannel | undefined =
  typeof BroadcastChannel === 'undefined' ? undefined : new BroadcastChannel(CANAL);

canal?.addEventListener('message', () => {
  avisarOuvintes();
});

function avisarOuvintes(): void {
  // Cópia antes de percorrer: um ouvinte pode se desinscrever durante o próprio aviso —
  // é o que acontece quando a rebusca desmonta o componente que a pediu.
  for (const ouvinte of [...ouvintes]) ouvinte();
}

/**
 * Anuncia que algo mudou.
 *
 * Chamado de um lugar só: o `useCommand`, depois de um comando bem-sucedido. É o funil por
 * onde toda escrita da interface passa, e concentrar o aviso ali é o que evita que cada
 * tela tenha de lembrar de avisar — lembrar é o que falha.
 *
 * Não diz **o que** mudou, de propósito. Um aviso por entidade exigiria que cada caso de
 * uso declarasse o que toca, e a conta dessa declaração é paga para sempre, em troca de
 * economizar consultas que já são baratas. Quando o servidor tiver canal e o volume
 * justificar, o parâmetro entra aqui sem que nenhuma tela mude.
 */
export function publishChange(): void {
  avisarOuvintes();
  canal?.postMessage(1);
}

/** Inscreve-se nas mudanças. Devolve a função que cancela. */
export function subscribeToChanges(ouvinte: Ouvinte): () => void {
  ouvintes.add(ouvinte);
  return () => ouvintes.delete(ouvinte);
}
